
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { mockServices } from '@/data/mockData';
import { useBooking } from '@/contexts/BookingContext';
import { motion } from 'framer-motion';
import { Calendar, Clock, Check } from 'lucide-react';
import { Provider, Service } from '@/types/model';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ServiceCard from '@/components/ServiceCard';

// Mock staff for demonstration purposes
const mockStaff = [
  {
    id: '1',
    name: 'Maria Schmidt',
    position: 'Senior Stylist',
    imageUrl: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1061&q=80',
    specialties: ['Haarschnitt', 'Färben', 'Styling'],
  },
  {
    id: '2',
    name: 'Thomas Weber',
    position: 'Haar-Experte',
    imageUrl: 'https://images.unsplash.com/photo-1566492031773-4f4e44671857?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=987&q=80',
    specialties: ['Herrenschnitt', 'Bart', 'Hochzeitsstyling'],
  },
  {
    id: '3',
    name: 'Sophie Müller',
    position: 'Coloristin',
    imageUrl: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=988&q=80',
    specialties: ['Balayage', 'Ombre', 'Highlights'],
  },
];

// Mock available dates and times
const mockDates = Array.from({ length: 14 }, (_, i) => {
  const date = new Date();
  date.setDate(date.getDate() + i);
  return date;
});

const mockTimeSlots = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30'
];

export interface StudioBookingPanelProps {
  provider: Provider;
  selectedService: Service | null;
  onServiceSelected: (service: Service) => void;
}

const StudioBookingPanel = ({ provider, selectedService, onServiceSelected }: StudioBookingPanelProps) => {
  const navigate = useNavigate();
  const {
    setSelectedProvider,
    setSelectedService,
    setSelectedStaff,
    setSelectedDate,
    setSelectedTime,
    setCurrentStep
  } = useBooking();
  
  const [services, setServices] = useState<Service[]>([]);
  const [selectedStaffMember, setSelectedStaffMember] = useState<any>(null);
  const [selectedDateLocal, setSelectedDateLocal] = useState<Date | null>(null);
  const [selectedTimeLocal, setSelectedTimeLocal] = useState<string | null>(null);
  const [currentStep, setCurrentStepLocal] = useState(1);
  
  useEffect(() => {
    // Load services for this provider
    setServices(mockServices[provider.id] || []);
  }, [provider]);
  
  // Function to proceed to booking
  const handleProceedToBooking = () => {
    // Save all selections to booking context
    setSelectedProvider(provider);
    if (selectedService) setSelectedService(selectedService);
    if (selectedStaffMember) setSelectedStaff(selectedStaffMember);
    if (selectedDateLocal) setSelectedDate(selectedDateLocal);
    if (selectedTimeLocal) setSelectedTime(selectedTimeLocal);
    
    // Determine which step to navigate to in the booking flow
    if (selectedService) {
      setCurrentStep(3); // Skip service selection
    } else {
      setCurrentStep(2); // Go to service selection
    }
    
    // Navigate to booking flow
    navigate('/booking');
  };
  
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('de-DE', { 
      weekday: 'short', 
      day: '2-digit', 
      month: '2-digit' 
    });
  };
  
  // Render different steps based on currentStep
  const renderStepContent = () => {
    switch (currentStep) {
      case 1: // Service selection
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium mb-4 text-chrona-text">Wähle eine Leistung</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {services.map(service => (
                <ServiceCard
                  key={service.id}
                  service={service}
                  selected={selectedService?.id === service.id}
                  onClick={() => {
                    onServiceSelected(service);
                    setCurrentStepLocal(2);
                  }}
                />
              ))}
            </div>
          </div>
        );
        
      case 2: // Staff selection
        return (
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-chrona-text">Wähle einen Mitarbeiter</h3>
              <Button 
                variant="ghost" 
                onClick={() => setCurrentStepLocal(1)}
                className="text-chrona-accent hover:text-chrona-accent-light"
              >
                Zurück
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {mockStaff.map(staff => (
                <Card 
                  key={staff.id} 
                  className={`cursor-pointer overflow-hidden transition-all duration-300 border ${
                    selectedStaffMember?.id === staff.id 
                      ? 'border-chrona-accent ring-1 ring-chrona-accent shadow-lg shadow-chrona-accent/20' 
                      : 'border-white/10'
                  } bg-black/40 backdrop-blur-xl`}
                  onClick={() => {
                    setSelectedStaffMember(staff);
                    setCurrentStepLocal(3);
                  }}
                >
                  <div className="aspect-square">
                    <img 
                      src={staff.imageUrl} 
                      alt={staff.name} 
                      className="w-full h-full object-cover object-center" 
                    />
                  </div>
                  <CardContent className="p-4">
                    <h3 className="text-lg font-medium text-chrona-text mb-1">{staff.name}</h3>
                    <p className="text-sm text-chrona-accent mb-3">{staff.position}</p>
                    
                    {staff.specialties && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {staff.specialties.map(specialty => (
                          <span 
                            key={specialty} 
                            className="inline-block px-2 py-1 bg-chrona-gray-800/40 text-chrona-gray-300 text-xs rounded-full"
                          >
                            {specialty}
                          </span>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );
        
      case 3: // Date and time selection
        return (
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-chrona-text">Wähle einen Termin</h3>
              <Button 
                variant="ghost" 
                onClick={() => setCurrentStepLocal(2)}
                className="text-chrona-accent hover:text-chrona-accent-light"
              >
                Zurück
              </Button>
            </div>
            
            {/* Date selection */}
            <div className="mb-6">
              <h4 className="text-sm font-medium mb-3 text-chrona-gray-300">Datum</h4>
              <div className="flex gap-2 overflow-x-auto pb-2 hide-scrollbar">
                {mockDates.map((date, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    className={`flex-shrink-0 ${
                      selectedDateLocal && date.toDateString() === selectedDateLocal.toDateString()
                        ? 'bg-chrona-accent text-chrona-text border-chrona-accent'
                        : 'bg-chrona-gray-800/40 text-chrona-text border-white/10 hover:bg-chrona-gray-700/60'
                    }`}
                    onClick={() => setSelectedDateLocal(date)}
                  >
                    {formatDate(date)}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Time selection */}
            {selectedDateLocal && (
              <div>
                <h4 className="text-sm font-medium mb-3 text-chrona-gray-300">Uhrzeit</h4>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                  {mockTimeSlots.map((time, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      className={`${
                        selectedTimeLocal === time
                          ? 'bg-chrona-accent text-chrona-text border-chrona-accent'
                          : 'bg-chrona-gray-800/40 text-chrona-text border-white/10 hover:bg-chrona-gray-700/60'
                      }`}
                      onClick={() => setSelectedTimeLocal(time)}
                    >
                      {time}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Continue button */}
            {selectedDateLocal && selectedTimeLocal && (
              <Button 
                className="w-full mt-6 bg-chrona-accent hover:bg-chrona-accent-light text-chrona-text"
                onClick={handleProceedToBooking}
              >
                Termin buchen
              </Button>
            )}
          </div>
        );
        
      default:
        return null;
    }
  };
  
  // Booking summary
  const renderBookingSummary = () => {
    if (!selectedService && currentStep === 1) return null;
    
    return (
      <Card className="mb-6 border border-white/10 bg-black/40 backdrop-blur-xl">
        <CardContent className="p-4">
          <h3 className="text-lg font-medium mb-3 text-chrona-text">Deine Auswahl</h3>
          
          <div className="space-y-2">
            {selectedService && (
              <div className="flex items-start gap-2">
                <Check size={16} className="text-chrona-accent mt-0.5" />
                <div>
                  <p className="font-medium text-chrona-text">{selectedService.name}</p>
                  <div className="flex items-center text-sm text-chrona-gray-400">
                    <Clock size={14} className="mr-1" />
                    <span>{selectedService.dauer} Min.</span>
                    <span className="mx-2">•</span>
                    <span>{selectedService.preis.toFixed(2)} €</span>
                  </div>
                </div>
              </div>
            )}
            
            {selectedStaffMember && (
              <div className="flex items-start gap-2">
                <Check size={16} className="text-chrona-accent mt-0.5" />
                <div>
                  <p className="font-medium text-chrona-text">{selectedStaffMember.name}</p>
                  <p className="text-sm text-chrona-gray-400">{selectedStaffMember.position}</p>
                </div>
              </div>
            )}
            
            {selectedDateLocal && selectedTimeLocal && (
              <div className="flex items-start gap-2">
                <Check size={16} className="text-chrona-accent mt-0.5" />
                <div>
                  <p className="font-medium text-chrona-text">
                    {selectedDateLocal.toLocaleDateString('de-DE', { 
                      weekday: 'long', 
                      day: '2-digit', 
                      month: '2-digit',
                      year: 'numeric'
                    })}
                  </p>
                  <p className="text-sm text-chrona-gray-400">um {selectedTimeLocal} Uhr</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };
  
  return (
    <div className="space-y-4">
      {renderBookingSummary()}
      {renderStepContent()}
    </div>
  );
};

export default StudioBookingPanel;
